/**
 * creature/update.ts — The Creature Tick
 *
 * Called each frame for every creature in the room. Runs the behavior AI,
 * updates movement along pathfinding results, lerps poses, applies
 * procedural animation layers, and decays needs.
 *
 * This is the creature equivalent of the player update in loop.ts,
 * but creatures are autonomous — they decide where to go and what to do.
 */

import type { CreatureState } from "./state.js";
import type { Direction } from "../engine/state.js";
import { lerpPose, applyPoseOverride } from "./pose.js";
import {
  evaluateTransition,
  poseForState,
  updateNeeds,
  applyProceduralLayers,
} from "./behavior.js";
import type { WalkableCheck } from "../pathfinding/astar.js";
import { findPath, manhattanDist } from "../pathfinding/astar.js";
import { getSpecies } from "./species.js";

// ─── Constants ──────────────────────────────────────────────────────────────

/** Behavior AI runs at reduced frequency for performance */
const BEHAVIOR_TICK_INTERVAL = 0.1; // 10Hz

/** Pose lerp rate (same as title.ts: 3.5 * dt gives ~0.3s blend) */
const POSE_LERP_RATE = 3.5;

/** How often chase creatures recalculate their path to the player (seconds) */
const CHASE_RETARGET_INTERVAL = 0.5;

/** Chase speed multiplier — predatory pursuit is faster than a stroll */
const CHASE_SPEED_MULTIPLIER = 1.5;

/** Melee range — within this many tiles the creature stops and attacks */
const CHASE_MELEE_RANGE = 1.2;

/** Disengage range — beyond this the creature gives up the chase */
const CHASE_DISENGAGE_RANGE = 6;

// ─── Creature Room Context ──────────────────────────────────────────────────

export interface CreatureUpdateContext {
  /** Time in seconds */
  time: number;
  /** Delta time */
  dt: number;
  /** Game hour (0-24) */
  gameHour: number;
  /** Room mood dimensions */
  mood: { warmth: number; energy: number; mystery: number; noise: number; nature: number };
  /** Player tile position */
  playerPos: { tileX: number; tileY: number };
  /** All creatures in the room (for social behavior) */
  allCreatures: CreatureState[];
  /** Walkability checker for the room */
  isWalkable: WalkableCheck;
  /** Room dimensions */
  roomWidth: number;
  roomHeight: number;
}

// ─── Main Update Function ───────────────────────────────────────────────────

/** Accumulator for throttled behavior ticks */
const behaviorAccumulators = new Map<string, number>();

/** Accumulator for chase path recalculation (separate from behavior tick) */
const chaseRetargetAccumulators = new Map<string, number>();

/**
 * Update a single creature for one frame.
 * Called from the game loop for each creature in the room.
 */
export function updateCreature(
  creature: CreatureState,
  ctx: CreatureUpdateContext
): void {
  // Skip if mounted (player controls movement)
  if (creature.mountState?.mounted) return;

  const species = getSpecies(creature.speciesId);
  if (!species) return;

  // ── 1. Behavior AI (throttled to 10Hz) ─────────────────────────
  let acc = behaviorAccumulators.get(creature.id) ?? 0;
  acc += ctx.dt;
  if (acc >= BEHAVIOR_TICK_INTERVAL) {
    acc -= BEHAVIOR_TICK_INTERVAL;
    updateBehavior(creature, ctx);
  }
  behaviorAccumulators.set(creature.id, acc);

  // ── 1b. Chase retargeting (throttled to ~2Hz) ──────────────────
  if (creature.behaviorState.current === "chase") {
    updateChaseRetarget(creature, ctx);
  }

  // ── 2. Movement ────────────────────────────────────────────────
  const moveSpeed = creature.behaviorState.current === "chase"
    ? species.baseSpeed * CHASE_SPEED_MULTIPLIER
    : species.baseSpeed;
  updateMovement(creature, ctx, moveSpeed);

  // ── 3. Pose interpolation ──────────────────────────────────────
  lerpPose(creature.pose, creature.targetPose, POSE_LERP_RATE * ctx.dt);

  // ── 4. Procedural animation layers ─────────────────────────────
  applyProceduralLayers(
    creature.pose,
    creature.behaviorState.current,
    ctx.time,
    ctx.dt,
    creature.walkDir
  );

  // ── 5. Needs decay ─────────────────────────────────────────────
  updateNeeds(creature.needs, creature.behaviorState.current, ctx.dt);
}

// ─── Behavior Update ────────────────────────────────────────────────────────

function updateBehavior(creature: CreatureState, ctx: CreatureUpdateContext): void {
  const bs = creature.behaviorState;
  bs.stateTime += BEHAVIOR_TICK_INTERVAL;

  // Chase state is managed by the combat system — never transition out via normal AI
  if (bs.current === "chase") return;

  // Check for state transition
  if (bs.stateTime >= bs.nextTransition) {
    const ownerDist = creature.ownerId
      ? manhattanDist(creature.position, ctx.playerPos)
      : 999;

    const result = evaluateTransition(bs.current, {
      genome: creature.genome,
      needs: creature.needs,
      mood: ctx.mood,
      gameHour: ctx.gameHour,
      ownerNearby: ownerDist <= 3,
      ownerDistance: ownerDist,
      otherCreatureCount: ctx.allCreatures.length - 1,
      bondLevel: creature.bondLevel,
    });

    const prevState = bs.current;
    bs.current = result.state;
    bs.nextTransition = result.duration;
    bs.stateTime = 0;

    // Set target pose for new state
    applyPoseOverride(creature.targetPose, poseForState(bs.current, ctx.time));

    // Set movement target for movement states
    if (bs.current === "wander" || bs.current === "explore" || bs.current === "patrol") {
      // 30% chance to revisit a favorite spot if available
      const favSpots = bs.favoriteSpots;
      let target: { tileX: number; tileY: number } | null = null;
      if (favSpots.length > 0 && Math.random() < 0.3) {
        // Weight by score
        const totalScore = favSpots.reduce((s, f) => s + f.score, 0);
        let r = Math.random() * totalScore;
        for (const spot of favSpots) {
          r -= spot.score;
          if (r <= 0) { target = spot; break; }
        }
      }
      if (!target) target = findRandomWalkable(creature, ctx);
      if (target) {
        setCreatureTarget(creature, target, ctx);
      }
    } else if (bs.current === "follow" && creature.ownerId) {
      // Move toward player
      setCreatureTarget(creature, ctx.playerPos, ctx);
    } else if (bs.current === "flee") {
      // Move away from player
      const fleeDirX = creature.position.tileX - ctx.playerPos.tileX;
      const fleeDirY = creature.position.tileY - ctx.playerPos.tileY;
      const len = Math.max(1, Math.abs(fleeDirX) + Math.abs(fleeDirY));
      const target = {
        tileX: Math.round(creature.position.tileX + (fleeDirX / len) * 3),
        tileY: Math.round(creature.position.tileY + (fleeDirY / len) * 3),
      };
      setCreatureTarget(creature, target, ctx);
    }

    // Socialization: move toward a nearby creature
    if (bs.current === "socialize" && ctx.allCreatures.length > 1) {
      const others = ctx.allCreatures.filter(c => c.id !== creature.id);
      const nearby = others.sort((a, b) =>
        manhattanDist(creature.position, a.position) - manhattanDist(creature.position, b.position)
      )[0];
      if (nearby && manhattanDist(creature.position, nearby.position) > 1) {
        setCreatureTarget(creature, nearby.position, ctx);
      }
      bs.interactionTarget = nearby?.id;
    }

    // Walk direction from state change
    if (bs.current === "wander" || bs.current === "explore") {
      creature.walkDir = Math.random() < 0.5 ? -1 : 1;
    }
  }
}

// ─── Chase Retarget ─────────────────────────────────────────────────────────

/**
 * Recalculate the chase path toward the player at throttled intervals.
 * The creature locks onto the player's position and adjusts its route
 * as the player moves — predatory, persistent, closing in.
 */
function updateChaseRetarget(creature: CreatureState, ctx: CreatureUpdateContext): void {
  let acc = chaseRetargetAccumulators.get(creature.id) ?? CHASE_RETARGET_INTERVAL;
  acc += ctx.dt;

  if (acc < CHASE_RETARGET_INTERVAL) {
    chaseRetargetAccumulators.set(creature.id, acc);
    return;
  }

  // Reset accumulator
  chaseRetargetAccumulators.set(creature.id, 0);

  const dist = manhattanDist(creature.position, ctx.playerPos);

  // Disengage — player escaped
  if (dist > CHASE_DISENGAGE_RANGE) {
    creature.behaviorState.current = "idle";
    creature.behaviorState.stateTime = 0;
    creature.behaviorState.nextTransition = 2 + Math.random() * 3;
    creature.movementQueue = [];
    chaseRetargetAccumulators.delete(creature.id);
    applyPoseOverride(creature.targetPose, poseForState("idle", ctx.time));
    return;
  }

  // Within melee range — stop moving, face the player, attack
  const tileDist = Math.sqrt(
    (creature.position.tileX - ctx.playerPos.tileX) ** 2 +
    (creature.position.tileY - ctx.playerPos.tileY) ** 2
  );
  if (tileDist <= CHASE_MELEE_RANGE) {
    creature.movementQueue = [];
    // Face the player while in melee range
    creature.direction = computeDirection(creature.position, ctx.playerPos);
    return;
  }

  // Recalculate path to player — the hunt continues
  const path = findPath(
    ctx.isWalkable,
    { tileX: creature.position.tileX, tileY: creature.position.tileY },
    ctx.playerPos,
    { maxLength: 16, allowDiagonal: true }
  );

  if (path && path.length > 1) {
    // Skip current tile, trim path to stop 1 tile short of player (melee range)
    const trimmed = path.slice(1);
    // Stop the path one tile before the destination so the creature
    // doesn't try to stand ON the player
    if (trimmed.length > 1) {
      creature.movementQueue = trimmed.slice(0, -1);
    } else {
      creature.movementQueue = trimmed;
    }
    creature.direction = computeDirection(creature.position, creature.movementQueue[0]);
    creature.walkDir = creature.movementQueue[0].tileX >= creature.position.tileX ? 1 : -1;
  }
}

// ─── Movement Update ────────────────────────────────────────────────────────

function updateMovement(
  creature: CreatureState,
  ctx: CreatureUpdateContext,
  baseSpeed: number
): void {
  if (creature.movementQueue.length === 0) return;

  const target = creature.movementQueue[0];
  creature.moveProgress += ctx.dt * baseSpeed;

  if (creature.moveProgress >= 1.0) {
    // Arrived at next tile
    creature.position.tileX = target.tileX;
    creature.position.tileY = target.tileY;
    creature.moveProgress = 0;
    creature.movementQueue.shift();

    // Remember favorite spots — places where the creature transitions to happy states
    if (creature.movementQueue.length === 0) {
      const bs = creature.behaviorState;
      if (bs.current === "sleep" || bs.current === "sit" || bs.current === "eat") {
        const spots = bs.favoriteSpots;
        const existing = spots.find(s =>
          s.tileX === creature.position.tileX && s.tileY === creature.position.tileY
        );
        if (existing) {
          existing.score = Math.min(10, existing.score + 0.5);
        } else if (spots.length < 5) {
          spots.push({ tileX: creature.position.tileX, tileY: creature.position.tileY, score: 1 });
        }
      }
    }

    // Update direction based on movement
    if (creature.movementQueue.length > 0) {
      creature.direction = computeDirection(
        creature.position,
        creature.movementQueue[0]
      );
    }
  }
}

// ─── Pathfinding Helpers ────────────────────────────────────────────────────

function setCreatureTarget(
  creature: CreatureState,
  target: { tileX: number; tileY: number },
  ctx: CreatureUpdateContext
): void {
  const path = findPath(
    ctx.isWalkable,
    { tileX: creature.position.tileX, tileY: creature.position.tileY },
    target,
    { maxLength: 12, allowDiagonal: true }
  );
  if (path && path.length > 1) {
    // Skip the first tile (current position)
    creature.movementQueue = path.slice(1);
    creature.direction = computeDirection(creature.position, creature.movementQueue[0]);
  }
}

function findRandomWalkable(
  creature: CreatureState,
  ctx: CreatureUpdateContext
): { tileX: number; tileY: number } | null {
  // Try a few random positions near the creature
  for (let i = 0; i < 10; i++) {
    const dx = Math.round((Math.random() - 0.5) * 6);
    const dy = Math.round((Math.random() - 0.5) * 6);
    const tx = creature.position.tileX + dx;
    const ty = creature.position.tileY + dy;
    if (tx >= 0 && ty >= 0 && tx < ctx.roomWidth && ty < ctx.roomHeight && ctx.isWalkable(tx, ty)) {
      return { tileX: tx, tileY: ty };
    }
  }
  return null;
}

function computeDirection(
  from: { tileX: number; tileY: number },
  to: { tileX: number; tileY: number }
): Direction {
  const dx = to.tileX - from.tileX;
  const dy = to.tileY - from.tileY;

  if (dx > 0 && dy > 0) return "SE";
  if (dx > 0 && dy < 0) return "NE";
  if (dx < 0 && dy > 0) return "SW";
  if (dx < 0 && dy < 0) return "NW";
  if (dx > 0) return "E";
  if (dx < 0) return "W";
  if (dy > 0) return "S";
  return "N";
}

// ─── Cleanup ────────────────────────────────────────────────────────────────

/** Remove behavior accumulator for a creature (call when creature leaves room) */
export function cleanupCreature(creatureId: string): void {
  behaviorAccumulators.delete(creatureId);
  chaseRetargetAccumulators.delete(creatureId);
}
